import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Radio, Play, Square, Loader2, Eye, ExternalLink, CheckCircle, XCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useStreamManagement, LiveStream } from "@/hooks/use-streams";

export function StreamManager() {
    const queryClient = useQueryClient();
    const { streamerProfile, myStreams, activeStream, isStarting, isEnding, isLoadingMyStreams } = useStreamManagement();
    const [showStartDialog, setShowStartDialog] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [activeImageLoaded, setActiveImageLoaded] = useState(false);
    const [historyImageLoaded, setHistoryImageLoaded] = useState<Record<string, boolean>>({});

   // Real-time viewer count updates
   const { data: liveStreamData } = useQuery({
     queryKey: ["active-stream-viewers", streamerProfile?.id],
     queryFn: async () => {
       if (!streamerProfile) return null;
       const { data, error } = await supabase
         .from("live_streams")
         .select("viewer_count")
         .eq("streamer_id", streamerProfile.id)
         .eq("is_live", true)
         .single();
       if (error && error.code !== 'PGRST116') throw error; // PGRST116 is no rows
       return data;
     },
     refetchInterval: 10000, // Update every 10 seconds
     enabled: !!streamerProfile && !!activeStream,
   });

  const validateTitle = (value: string) => {
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, title: 'Title is required' }));
      return false;
    }
    if (value.length < 3) {
      setErrors(prev => ({ ...prev, title: 'Title must be at least 3 characters' }));
      return false;
    }
    if (value.length > 100) {
      setErrors(prev => ({ ...prev, title: 'Title must be less than 100 characters' }));
      return false;
    }
    setErrors(prev => ({ ...prev, title: '' }));
    return true;
  };

  const validateUrl = (value: string) => {
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, url: 'URL is required' }));
      return false;
    }
    try {
      new URL(value);
      setErrors(prev => ({ ...prev, url: '' }));
      return true;
    } catch {
      setErrors(prev => ({ ...prev, url: 'Invalid URL format' }));
      return false;
    }
  };

  const [form, setForm] = useState({
    platform: "twitch",
    title: "",
    url: "",
    thumbnail: "",
    tags: "",
  });

  useEffect(() => {
    validateTitle(form.title);
    validateUrl(form.url);
  }, [form.title, form.url]);

  const startStreamMutation = useMutation({
    mutationFn: async () => {
      if (!streamerProfile) throw new Error("You must be a registered streamer");

      const { error } = await supabase.from("live_streams").insert({
        streamer_id: streamerProfile.id,
        platform: form.platform,
        title: form.title,
        url: form.url,
        thumbnail: form.thumbnail || null,
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
        is_live: true,
        started_at: new Date().toISOString(),
        viewer_count: 0,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-streams"] });
      queryClient.invalidateQueries({ queryKey: ["live-streams"] });
      toast({ title: "Stream Started!", description: "You are now live!" });
      setShowStartDialog(false);
      setForm({ platform: "twitch", title: "", url: "", thumbnail: "", tags: "" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const endStreamMutation = useMutation({
    mutationFn: async (streamId: string) => {
      const { error } = await supabase
        .from("live_streams")
        .update({ is_live: false, ended_at: new Date().toISOString() })
        .eq("id", streamId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-streams"] });
      queryClient.invalidateQueries({ queryKey: ["live-streams"] });
      toast({ title: "Stream Ended" });
    },
  });

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case "twitch": return "💜";
      case "youtube": return "🎬";
      case "tiktok": return "📱";
      case "kick": return "🟢";
      default: return "📺";
    }
  };

  const formatDuration = (startedAt: string | null | undefined) => {
    if (!startedAt) return "0:00";
    const start = new Date(startedAt).getTime();
    const now = Date.now();
    const diff = Math.floor((now - start) / 1000);
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  if (!streamerProfile) {
    return (
      <div className="p-6 rounded-xl bg-card border border-border text-center">
        <Radio className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
        <p className="text-muted-foreground">Register as a streamer to manage streams</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Stream */}
      {activeStream ? (
        <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/20 to-destructive/5 border border-destructive/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-destructive animate-pulse" />
              <h3 className="font-semibold text-foreground">Currently Live</h3>
              <Badge variant="destructive">{formatDuration(activeStream.started_at)}</Badge>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => endStreamMutation.mutate(activeStream.id)}
              disabled={endStreamMutation.isPending}
            >
              {endStreamMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <><Square className="w-4 h-4" /> End Stream</>
              )}
            </Button>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-32 h-20 rounded-lg bg-secondary overflow-hidden flex-shrink-0 relative">
              {activeStream.thumbnail ? (
                <>
                  {!activeImageLoaded && <Skeleton className="absolute inset-0" />}
                  <img
                    src={activeStream.thumbnail}
                    alt=""
                    className={cn("w-full h-full object-cover", !activeImageLoaded && "opacity-0")}
                    onLoad={() => setActiveImageLoaded(true)}
                    onError={() => setActiveImageLoaded(true)}
                  />
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Play className="w-8 h-8 text-muted-foreground/30" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-foreground truncate">{activeStream.title}</h4>
              <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                <span>{getPlatformIcon(activeStream.platform)} {activeStream.platform}</span>
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {liveStreamData?.viewer_count || activeStream.viewer_count || 0} viewers
                </span>
              </div>
              <a
                href={activeStream.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline text-sm mt-2"
              >
                <ExternalLink className="w-3 h-3" /> Watch Stream
              </a>
            </div>
          </div>
        </div>
      ) : (
        <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
          <DialogTrigger asChild>
            <Button variant="hero" className="w-full">
              <Radio className="w-4 h-4" />
              Start New Stream
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start Live Stream</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              <div>
                <Label className="mb-2 block">Platform *</Label>
                <Select value={form.platform} onValueChange={(v) => setForm(f => ({ ...f, platform: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twitch">💜 Twitch</SelectItem>
                    <SelectItem value="youtube">🎬 YouTube</SelectItem>
                    <SelectItem value="tiktok">📱 TikTok</SelectItem>
                    <SelectItem value="kick">🟢 Kick</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">Stream Title *</Label>
                <Input
                  placeholder="Trading $MOON live! 🚀"
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                />
                {errors.title !== undefined && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${errors.title ? 'text-destructive' : 'text-success'}`}>
                    {errors.title ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                    {errors.title || 'Valid'}
                  </p>
                )}
              </div>

              <div>
                <Label className="mb-2 block">Stream URL *</Label>
                <Input
                  placeholder="https://twitch.tv/yourstream"
                  value={form.url}
                  onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
                />
                {errors.url !== undefined && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${errors.url ? 'text-destructive' : 'text-success'}`}>
                    {errors.url ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                    {errors.url || 'Valid'}
                  </p>
                )}
              </div>

              <div>
                <Label className="mb-2 block">Thumbnail URL</Label>
                <Input
                  placeholder="https://..."
                  value={form.thumbnail}
                  onChange={(e) => setForm(f => ({ ...f, thumbnail: e.target.value }))}
                />
              </div>

              <div>
                <Label className="mb-2 block">Tags (comma separated)</Label>
                <Input
                  placeholder="crypto, trading, solana"
                  value={form.tags}
                  onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
                />
              </div>

              <Button
                variant="hero"
                className="w-full"
                onClick={() => startStreamMutation.mutate()}
                disabled={!form.title || !form.url || startStreamMutation.isPending || errors.title || errors.url}
              >
                {startStreamMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Starting...</>
                ) : (
                  <><Play className="w-4 h-4" /> Go Live</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Stream History */}
      <div>
        <h3 className="font-semibold text-foreground mb-4">Stream History</h3>
        {isLoadingMyStreams ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-4 rounded-lg bg-secondary/50 flex items-center gap-4">
                <Skeleton className="w-16 h-10 rounded" />
                <div className="flex-1 min-w-0">
                  <Skeleton className="h-4 w-32 mb-1" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        ) : myStreams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <Radio className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No Streams Yet</h3>
            <p className="text-muted-foreground max-w-sm">
              Start your first live stream to engage with the community.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {myStreams.filter(s => !s.is_live).slice(0, 10).map((stream) => (
              <div key={stream.id} className="p-4 rounded-lg bg-secondary/50 flex items-center gap-4">
                <div className="w-16 h-10 rounded bg-secondary overflow-hidden flex-shrink-0 relative">
                  {stream.thumbnail ? (
                    <>
                      {!historyImageLoaded[stream.id] && <Skeleton className="absolute inset-0" />}
                      <img
                        src={stream.thumbnail}
                        alt=""
                        className={cn("w-full h-full object-cover", !historyImageLoaded[stream.id] && "opacity-0")}
                        onLoad={() => setHistoryImageLoaded(prev => ({ ...prev, [stream.id]: true }))}
                        onError={() => setHistoryImageLoaded(prev => ({ ...prev, [stream.id]: true }))}
                      />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Play className="w-4 h-4 text-muted-foreground/30" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-foreground truncate">{stream.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {getPlatformIcon(stream.platform)} {stream.platform} • {stream.viewer_count || 0} peak viewers
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  {stream.started_at ? new Date(stream.started_at).toLocaleDateString() : ""}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
